import { useCallback, useEffect, useRef, useState } from 'react';
import type { Api, Contact, NumberKind } from '../api';
import { LANGUAGES, catalogue, type T } from '../i18n';
import { PERM, useCan } from '../access';
import { DangerButton, Field, ReadOnlyNote } from '../ui';
import { ScoreBadge } from './Leads';
import { errorText } from './AgentSettings';

type Parsed = { rows: Array<{ name: string; phone: string; fields: Record<string, string>; numberKind?: NumberKind }>; columns: string[] };

/** BRAIN/35 Faz 4: the words a number-type column and its values may be written with — from every panel language. */
export type KindWords = { header: Set<string>; business: Set<string>; person: Set<string>; unknown: Set<string> };
const fold = (v: string) => v.toLowerCase().normalize('NFKD').replace(/\p{M}/gu, '').replace(/ı/gu, 'i').replace(/\s+/gu, ' ').trim();
export const kindWords = (): KindWords => {
  const words = (key: string) => new Set(LANGUAGES.flatMap((l) => (catalogue(l.code)[key] ?? '').split('|')).map(fold).filter(Boolean));
  return { header: words('contacts.kind.header.aliases'), business: words('contacts.kind.business.aliases'), person: words('contacts.kind.person.aliases'), unknown: words('contacts.kind.unknown.aliases') };
};
const kindOf = (v: string, w: KindWords): NumberKind | undefined => {
  const x = fold(v);
  return w.business.has(x) ? 'business' : w.person.has(x) ? 'person' : w.unknown.has(x) ? 'unknown' : undefined;
};

/**
 * Rows pasted from a spreadsheet or a CSV file: the phone column is the one with the most digits, the name the first other
 * text column. With `words`, a column headed like "number type" (any panel language) gives each row's number kind.
 */
export const parseContacts = (text: string, words?: KindWords): Parsed => {
  const lines = text.split(/\r?\n/u).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return { rows: [], columns: [] };
  const delim = lines[0]!.includes('\t') ? '\t' : lines[0]!.split(';').length > lines[0]!.split(',').length ? ';' : ',';
  const cells = lines.map((l) => l.split(delim).map((c) => c.trim().replace(/^"(.*)"$/u, '$1')));
  const width = Math.max(...cells.map((r) => r.length));
  const phoneLike = (v: string) => (v.match(/\d/gu)?.length ?? 0) >= 7 && /^[+\d\s().-]+$/u.test(v);
  const score = (i: number) => cells.filter((r) => phoneLike(r[i] ?? '')).length;
  let phoneCol = 0;
  for (let i = 1; i < width; i += 1) if (score(i) > score(phoneCol)) phoneCol = i;
  const header = !phoneLike(cells[0]![phoneCol] ?? '');
  const names = header ? cells[0]! : Array.from({ length: width }, (_, i) => `col_${i + 1}`);
  const body = header ? cells.slice(1) : cells;
  const kindCol = header && words ? names.findIndex((n) => words.header.has(fold(n))) : -1;
  const nameCol = Array.from({ length: width }, (_, i) => i).find((i) => i !== phoneCol && i !== kindCol) ?? -1;
  return {
    columns: names,
    rows: body.map((r) => {
      const kind = kindCol >= 0 && words ? kindOf(r[kindCol] ?? '', words) : undefined;
      return {
        name: nameCol >= 0 ? r[nameCol] ?? '' : '',
        phone: r[phoneCol] ?? '',
        fields: Object.fromEntries(names.map((n, i) => [n, r[i] ?? '']).filter(([, v], i) => i !== phoneCol && i !== nameCol && i !== kindCol && v !== '')),
        ...(kind ? { numberKind: kind } : {}),
      };
    }),
  };
};

/** The fields a person can be saved with (ADR-058); the agent sees them during the call. Other columns of an import stay as they are. */
export const CONTACT_FIELDS = ['title', 'company', 'city', 'email', 'note'] as const;
type Form = { name: string; phone: string; numberKind: NumberKind } & Record<(typeof CONTACT_FIELDS)[number], string>;
const EMPTY: Form = { name: '', phone: '', numberKind: 'unknown', title: '', company: '', city: '', email: '', note: '' };
const KINDS: readonly NumberKind[] = ['business', 'person', 'unknown'];
const phoneOk = (v: string) => (v.match(/\d/gu)?.length ?? 0) >= 7;

/** Contacts (ADR-028, ADR-058): one person at a time (with title, company…) or many from a spreadsheet; the campaigns call them. */
export const ContactsPage = ({ api, t }: { api: Api; t: T }) => {
  // ADR-071: adding, editing, importing and removing need centeros.contacts; the list is for everyone who may see.
  const canEdit = useCan()(PERM.contacts);
  const [rows, setRows] = useState<Contact[]>();
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState<NumberKind | ''>('');
  const [form, setForm] = useState<Form>(EMPTY);
  const [editing, setEditing] = useState<string | null>(null);
  const [paste, setPaste] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string }>();
  const file = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    api.contacts(q, 500, kind || undefined).then((r) => { setRows(r.contacts); setTotal(r.total); }).catch((e: unknown) => setMsg({ kind: 'error', text: errorText(t, e) }));
  }, [api, q, kind, t]);
  useEffect(() => {
    const id = setTimeout(load, 250);
    return () => clearTimeout(id);
  }, [load]);

  const save = async (list: Array<{ name: string; phone: string; fields: Record<string, string>; numberKind?: NumberKind }>, after: () => void) => {
    setBusy(true);
    setMsg(undefined);
    try {
      const r = await api.importContacts(list);
      setMsg(r.invalid.length ? { kind: 'error', text: t('contacts.imported', { inserted: r.inserted, updated: r.updated, invalid: r.invalid.length }) } : { kind: 'ok', text: t('contacts.imported', { inserted: r.inserted, updated: r.updated, invalid: 0 }) });
      if (!r.invalid.length) after();
      load();
    } catch (e) {
      setMsg({ kind: 'error', text: errorText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  const saveOne = () =>
    void save(
      [{ name: form.name.trim(), phone: form.phone.trim(), numberKind: form.numberKind, fields: Object.fromEntries(CONTACT_FIELDS.map((k) => [k, form[k].trim()]).filter(([, v]) => v)) }],
      () => { setForm(EMPTY); setEditing(null); },
    );
  const edit = (c: Contact) => {
    const f = c.fields as Record<string, unknown>;
    setForm({ name: c.name, phone: c.phone, numberKind: c.numberKind ?? 'unknown', ...Object.fromEntries(CONTACT_FIELDS.map((k) => [k, typeof f[k] === 'string' ? (f[k] as string) : ''])) } as Form);
    setEditing(c.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const parsed = parseContacts(paste, kindWords());
  const set = (k: keyof Form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const extra = (c: Contact) => Object.entries(c.fields).filter(([k]) => k !== 'title' && k !== 'company').map(([k, v]) => `${t(`contacts.field.${k}`) === `contacts.field.${k}` ? k : t(`contacts.field.${k}`)}: ${String(v)}`).join(' · ');

  return (
    <section>
      <header className="page-head">
        <div>
          <h1>{t('contacts.title')}</h1>
          <p className="muted">{t('contacts.subtitle', { n: total })}</p>
        </div>
      </header>
      <ReadOnlyNote t={t} need={PERM.contacts} />
      {!canEdit && msg && <div className={`banner ${msg.kind} small`}>{msg.text}</div>}
      {canEdit && (
      <>
      <div className="card">
        <h3>{editing ? t('contacts.form.editTitle') : t('contacts.form.title')}</h3>
        <p className="hint">{t('contacts.form.hint')}</p>
        <div className="form-grid">
          <Field label={t('contacts.field.name')}><input value={form.name} onChange={set('name')} placeholder={t('contacts.field.name.placeholder')} /></Field>
          <Field label={`${t('contacts.field.phone')} *`} hint={t('contacts.field.phone.hint')}><input value={form.phone} onChange={set('phone')} placeholder="+90 5xx xxx xx xx" inputMode="tel" dir="ltr" disabled={editing !== null} /></Field>
          <Field label={t('contacts.kind.label')} hint={t('contacts.kind.hint')}>
            <select value={form.numberKind} onChange={(e) => setForm((f) => ({ ...f, numberKind: e.target.value as NumberKind }))}>
              {KINDS.map((k) => <option key={k} value={k}>{t(`contacts.kind.${k}`)}</option>)}
            </select>
          </Field>
          <Field label={`${t('contacts.field.title')} (${t('contacts.optional')})`}><input value={form.title} onChange={set('title')} placeholder={t('contacts.field.title.placeholder')} /></Field>
          <Field label={`${t('contacts.field.company')} (${t('contacts.optional')})`}><input value={form.company} onChange={set('company')} /></Field>
          <Field label={`${t('contacts.field.city')} (${t('contacts.optional')})`}><input value={form.city} onChange={set('city')} /></Field>
          <Field label={`${t('contacts.field.email')} (${t('contacts.optional')})`}><input value={form.email} onChange={set('email')} type="email" dir="ltr" /></Field>
        </div>
        <Field label={`${t('contacts.field.note')} (${t('contacts.optional')})`} hint={t('contacts.field.note.hint')}><textarea rows={2} value={form.note} onChange={set('note')} /></Field>
        <div className="row" style={{ marginTop: 10, gap: 8 }}>
          <button type="button" className="primary" disabled={busy || !phoneOk(form.phone)} onClick={saveOne}>{busy ? t('settings.saving') : t('contacts.form.save')}</button>
          {editing && <button type="button" className="ghost" onClick={() => { setForm(EMPTY); setEditing(null); }}>{t('contacts.form.cancel')}</button>}
        </div>
        {msg && <div className={`banner ${msg.kind} small`}>{msg.text}</div>}
      </div>
      <div className="card">
        <div className="row-between">
          <button type="button" className="link" onClick={() => setBulkOpen((x) => !x)}>{bulkOpen ? '▾' : '▸'} {t('contacts.import')}</button>
          {bulkOpen && (
            <span className="row">
              <input ref={file} type="file" accept=".csv,.txt,.tsv" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void f.text().then(setPaste); e.target.value = ''; }} />
              <button type="button" className="ghost" onClick={() => file.current?.click()}>{t('contacts.file')}</button>
            </span>
          )}
        </div>
        {bulkOpen && (
          <>
            <p className="hint">{t('contacts.import.hint')}</p>
            <textarea rows={4} value={paste} placeholder={t('contacts.import.placeholder')} onChange={(e) => setPaste(e.target.value)} />
            <div className="row-between" style={{ marginTop: 10 }}>
              <span className="hint">{parsed.rows.length ? t('contacts.preview', { n: parsed.rows.length, columns: parsed.columns.join(', ') }) : t('contacts.import.empty')}</span>
              <button type="button" className="primary" disabled={busy || parsed.rows.length === 0} onClick={() => void save(parsed.rows, () => setPaste(''))}>{busy ? t('settings.saving') : t('contacts.add', { n: parsed.rows.length })}</button>
            </div>
          </>
        )}
      </div>
      </>
      )}
      <div className="toolbar section-gap">
        <input className="search" type="search" value={q} placeholder={t('contacts.search')} onChange={(e) => setQ(e.target.value)} />
        <div className="segmented" role="group" aria-label={t('contacts.kind.label')}>
          {(['', ...KINDS] as const).map((k) => (
            <button key={k || 'all'} type="button" className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>{t(`contacts.kind.${k || 'all'}`)}</button>
          ))}
        </div>
      </div>
      {rows && rows.length === 0 && <div className="empty"><p>{t('contacts.empty')}</p></div>}
      {rows && rows.length > 0 && (
        <table className="table">
          <thead><tr><th>{t('contacts.col.name')}</th><th>{t('contacts.col.kind')}</th><th>{t('contacts.field.title')}</th><th>{t('contacts.field.company')}</th><th>{t('calls.col.number')}</th><th>{t('contacts.col.fields')}</th><th>{t('contacts.col.stage')}</th>{canEdit && <th />}</tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>{c.name || <span className="muted">—</span>}</td>
                <td><span className={`tag ${c.numberKind === 'business' ? 'ok' : ''}`}>{t(`contacts.kind.${c.numberKind ?? 'unknown'}`)}</span></td>
                <td>{String((c.fields as Record<string, unknown>).title ?? '') || <span className="muted">—</span>}</td>
                <td>{String((c.fields as Record<string, unknown>).company ?? '') || <span className="muted">—</span>}</td>
                <td className="mono" dir="ltr">{c.phone}</td>
                <td className="muted small">{extra(c)}</td>
                <td><span className="row"><span className={`stage-dot ${c.stage ?? 'new'}`} />{t(`leads.stage.${c.stage ?? 'new'}`)}<ScoreBadge score={c.score} t={t} /></span></td>
                {canEdit && (
                  <td>
                    <span className="row">
                      <button type="button" className="ghost small" onClick={() => edit(c)}>{t('contacts.form.edit')}</button>
                      <DangerButton t={t} label={t('settings.remove')} need={PERM.contacts} onConfirm={() => void api.removeContact(c.id).then(load).catch((e: unknown) => setMsg({ kind: 'error', text: errorText(t, e) }))} />
                    </span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};
